import { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/hooks/useLanguage';

type PageState = 'loading' | 'form' | 'success' | 'error';

export function ResetPasswordPage() {
  const { t } = useLanguage();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setPageState('error');
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setPageState('form');
      } else if (event === 'SIGNED_IN' && pageState === 'loading') {
        setPageState('form');
      }
    });

    // Check if there's already a session (token already parsed)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setPageState('form');
      else if (pageState === 'loading') {
        // No session and no token — likely an invalid/expired link
        setTimeout(() => setPageState(s => s === 'loading' ? 'error' : s), 3000);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError(t.resetPassword.tooShort);
      return;
    }
    if (password !== confirmPassword) {
      setError(t.resetPassword.mismatch);
      return;
    }
    if (!supabase) return;

    setSaving(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
      } else {
        setPageState('success');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl bg-[var(--bg-primary)] border border-[var(--border)] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-[var(--border)]">
          <p className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">Trip<span style={{ background: 'linear-gradient(to right, #f59e0b, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Boom</span></p>
        </div>

        <div className="p-6">
          {pageState === 'loading' && (
            <div className="flex flex-col items-center gap-3 py-4 text-[var(--text-secondary)]">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-teal)]" />
              <p className="text-sm">{t.resetPassword.verifying}</p>
            </div>
          )}

          {pageState === 'error' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <AlertCircle className="w-10 h-10 text-[var(--status-red)]" />
              <p className="font-medium text-[var(--text-primary)]">{t.resetPassword.linkExpired}</p>
              <p className="text-sm text-[var(--text-secondary)]">
                {t.resetPassword.linkExpiredBody}
              </p>
              <a
                href="/"
                className="mt-2 w-full py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] font-medium text-center hover:border-[var(--accent-teal)]/40 transition-colors block"
              >
                {t.resetPassword.backToTripBoom}
              </a>
            </div>
          )}

          {pageState === 'success' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-[var(--accent-teal)]" />
              <p className="font-medium text-[var(--text-primary)]">{t.resetPassword.updated}</p>
              <p className="text-sm text-[var(--text-secondary)]">{t.resetPassword.updatedBody}</p>
              <a
                href="/"
                className="mt-2 w-full py-3 rounded-xl font-medium text-white text-center block"
                style={{ background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)' }}
              >
                {t.resetPassword.goToTripBoom}
              </a>
            </div>
          )}

          {pageState === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <h2 className="text-base font-medium text-[var(--text-primary)] mb-1">{t.resetPassword.setNew}</h2>
                <p className="text-sm text-[var(--text-secondary)]">{t.resetPassword.setNewBody}</p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-[var(--status-red-bg)] border border-[var(--status-red)]/20 text-[var(--status-red)] text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs text-[var(--text-secondary)] font-medium">{t.resetPassword.newPassword}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t.resetPassword.atLeast6}
                    autoFocus
                    className="w-full pl-10 pr-10 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                  />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-[var(--text-secondary)] font-medium">{t.resetPassword.confirmPassword}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={t.resetPassword.repeatPassword}
                    className="w-full pl-10 pr-10 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                  />
                  <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 rounded-xl font-medium text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)' }}
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.resetPassword.saving}</> : t.resetPassword.submit}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
